import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createTheme, ThemeProvider, useMediaQuery } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import json2mq from "json2mq";

// Data and settings
import {
  startingLocation,
  startingZoomLevel,
  GOOGLE_API_KEY,
  componentMode,
} from "./imports/data";

// Components
import Estimator from "./estimator/estimator";
import { MeasurItTitleBar } from "./interface/titlebar";
import WelcomeTour from "./components/tours/WelcomeTour";
import ColorGuideTour from "./components/tours/ColorGuideTour";

// Hooks
import useDebounce from "./hooks/useDebounce";
import { useGeocodeByAddress, useReverseGeocode } from "./hooks/useGeocode";
import { useUploadEstimate } from "./hooks/useEstimates";

// Constants and utilities
import { shortQueries } from "./queries";
import { COLORS, BRUSH_SIZES, PRELOAD_IMAGES, THEME_CONFIG } from "./constants/config";
import { preloadImages, getMapDimensions, getMeasurement, extractRooftopAddress } from "./utils";

/**
 * GeocodeRequestManager
 * 
 * Singleton module that manages geocode API requests with:
 * - Request deduplication (same address returns cached result)
 * - Debouncing (rapid requests are coalesced)
 * - In-flight request tracking (prevents duplicate concurrent requests)
 * - LRU cache with TTL for geocode results
 */
const GeocodeRequestManager = (() => {
  // Configuration
  const CONFIG = Object.freeze({
    DEBOUNCE_DELAY_MS: 300,
    CACHE_TTL_MS: 5 * 60 * 1000, // 5 minutes
    MAX_CACHE_SIZE: 50,
  });

  // Private state
  let _cache = new Map();
  let _pendingRequest = null;
  let _lastRequestedAddress = null;
  let _debounceTimer = null;
  let _abortController = null;

  /**
   * Normalizes an address for consistent cache key generation
   * @param {string} address - Raw address string
   * @returns {string} Normalized address
   */
  function normalizeAddress(address) {
    if (!address || typeof address !== 'string') return '';
    return address
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/,\s*/g, ', ');
  }

  /**
   * Generates a cache key from an address
   * @param {string} address - Address to generate key for
   * @returns {string} Cache key
   */
  function getCacheKey(address) {
    return normalizeAddress(address);
  }

  /**
   * Retrieves a cached result if valid
   * @param {string} address - Address to look up
   * @returns {Object|null} Cached result or null
   */
  function getFromCache(address) {
    const key = getCacheKey(address);
    const cached = _cache.get(key);
    
    if (!cached) return null;
    
    // Check TTL
    if (Date.now() - cached.timestamp > CONFIG.CACHE_TTL_MS) {
      _cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  /**
   * Stores a result in cache with LRU eviction
   * @param {string} address - Address key
   * @param {Object} data - Data to cache
   */
  function setInCache(address, data) {
    const key = getCacheKey(address);
    
    // LRU eviction if cache is full
    if (_cache.size >= CONFIG.MAX_CACHE_SIZE) {
      const oldestKey = _cache.keys().next().value;
      _cache.delete(oldestKey);
    }
    
    _cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Checks if this is a duplicate request
   * @param {string} address - Address to check
   * @returns {boolean} True if duplicate
   */
  function isDuplicateRequest(address) {
    return normalizeAddress(address) === normalizeAddress(_lastRequestedAddress);
  }

  /**
   * Performs the actual geocode API request
   * @param {string} address - Address to geocode
   * @param {AbortSignal} signal - Abort signal for cancellation
   * @returns {Promise<Object>} Geocode result
   */
  async function executeGeocodeRequest(address, signal) {
    const fetchUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_API_KEY}`;
    
    const response = await fetch(fetchUrl, { signal });
    
    if (!response.ok) {
      throw new Error(`Geocode request failed with status ${response.status}`);
    }
    
    const body = await response.json();
    
    if (body.status !== "OK") {
      throw new Error(`Geocode API returned status: ${body.status}`);
    }
    
    if (!body.results || body.results.length === 0) {
      throw new Error("No results found for address");
    }
    
    return body.results[0];
  }

  /**
   * Main geocode method with all optimizations
   * @param {string} address - Address to geocode
   * @param {Function} onSuccess - Success callback with location data
   * @param {Function} onError - Error callback
   * @param {Function} onStart - Called when request starts (for loading state)
   * @param {Function} onComplete - Called when request completes (success or error)
   */
  function geocode(address, { onSuccess, onError, onStart, onComplete }) {
    // Guard: Empty address
    if (!address || !address.trim()) {
      onError?.(new Error("Empty address provided"));
      return;
    }

    const normalizedAddress = normalizeAddress(address);

    // Check cache first - if cached, return immediately (no duplicate check needed)
    const cachedResult = getFromCache(address);
    if (cachedResult) {
      console.log('[GeocodeRequestManager] Cache hit for:', normalizedAddress);
      // Always call onSuccess for cache hits to ensure map updates
      onSuccess?.(cachedResult);
      return;
    }

    // Only check for duplicate if there's a pending request for the EXACT same address
    // This prevents duplicate API calls, not duplicate map updates
    if (isDuplicateRequest(address) && _pendingRequest) {
      console.log('[GeocodeRequestManager] Duplicate in-flight request ignored:', normalizedAddress);
      return;
    }

    // Clear any pending debounce timer
    if (_debounceTimer) {
      clearTimeout(_debounceTimer);
      _debounceTimer = null;
    }

    // Abort any in-flight request for a different address
    if (_abortController && _lastRequestedAddress && !isDuplicateRequest(address)) {
      console.log('[GeocodeRequestManager] Aborting previous request for:', _lastRequestedAddress);
      _abortController.abort();
      _abortController = null;
      _pendingRequest = null;
    }

    // Update last requested address
    _lastRequestedAddress = address;

    // Debounce the request
    _debounceTimer = setTimeout(async () => {
      _debounceTimer = null;

      // Double-check cache after debounce (another request might have populated it)
      const cachedAfterDebounce = getFromCache(address);
      if (cachedAfterDebounce) {
        console.log('[GeocodeRequestManager] Cache hit after debounce:', normalizedAddress);
        onSuccess?.(cachedAfterDebounce);
        return;
      }

      // Create new abort controller
      _abortController = new AbortController();
      
      onStart?.();
      console.log('[GeocodeRequestManager] Executing geocode request for:', normalizedAddress);

      try {
        _pendingRequest = executeGeocodeRequest(address, _abortController.signal);
        const result = await _pendingRequest;
        
        // Cache the successful result
        setInCache(address, result);
        
        onSuccess?.(result);
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('[GeocodeRequestManager] Request aborted for:', normalizedAddress);
          return; // Don't call onError for intentional aborts
        }
        console.error('[GeocodeRequestManager] Geocode error:', error);
        onError?.(error);
      } finally {
        _pendingRequest = null;
        _abortController = null;
        onComplete?.();
      }
    }, CONFIG.DEBOUNCE_DELAY_MS);
  }

  /**
   * Clears the cache (useful for testing)
   */
  function clearCache() {
    _cache.clear();
    _lastRequestedAddress = null;
  }

  /**
   * Cancels any pending request
   */
  function cancelPending() {
    if (_debounceTimer) {
      clearTimeout(_debounceTimer);
      _debounceTimer = null;
    }
    if (_abortController) {
      _abortController.abort();
      _abortController = null;
    }
    _pendingRequest = null;
  }

  // Public API
  return Object.freeze({
    geocode,
    clearCache,
    cancelPending,
  });
})();

/*
  This component serves as the controlling parent for
  the "dumb" estimator component.  Top-level functionality
  and state will be stored here and passed into the 
  subcomponents.
*/

// Create MUI theme
const theme = createTheme(THEME_CONFIG);

/**
 * Main App Component
 */
export default function App() {
  // Core state
  const [mode, setMode] = useState(componentMode);
  const [mapCenter, setMapCenter] = useState(startingLocation);
  const [mapBounds, setMapBounds] = useState(null);
  const [address, setAddress] = useState("");
  
  // Measurement state
  const [coverageSquareArea, setCoverageSquareArea] = useState(0);
  const [drawnLinearDistance, setDrawnLinearDistance] = useState(0);
  
  // UI state
  const [loaded, setLoaded] = useState(false);
  const [tourOpen, setTourOpen] = useState(true);
  const [drawTourOpen, setDrawTourOpen] = useState(false);

  // Hooks
  const debouncedMapCenter = useDebounce(mapCenter, 1000);
  const short = useMediaQuery(json2mq(shortQueries));
  const geocodeMutation = useGeocodeByAddress();
  const uploadMutation = useUploadEstimate();

  // Reverse geocode when map center changes (debounced)
  const { data: reverseGeocodeData } = useReverseGeocode(
    debouncedMapCenter?.lat,
    debouncedMapCenter?.lng,
    loaded
  );

  // Calculate derived values
  const measurement = useMemo(
    () => getMeasurement(mode, coverageSquareArea, drawnLinearDistance),
    [mode, coverageSquareArea, drawnLinearDistance]
  );

  const { width: mapWidth, height: mapHeight } = useMemo(
    () => getMapDimensions(window.innerWidth, window.innerHeight),
    []
  );

  const zoomLevel = useMemo(
    () => (short ? startingZoomLevel - 1 : startingZoomLevel),
    [short]
  );

  // Refs for tracking processed addresses and preventing duplicate handling
  const lastProcessedAddressRef = useRef(null);
  const isProcessingRef = useRef(false);

  /**
   * Handles the setAddressString message from parent window
   * Uses GeocodeRequestManager for deduplication, caching, and debouncing
   */
  const handleSetAddressString = useCallback(({ address: newAddress }) => {
    // Guard: Empty address
    if (!newAddress || !newAddress.trim()) {
      console.log('[App] Empty address received, ignoring');
      return;
    }

    // Guard: Prevent processing while already processing
    if (isProcessingRef.current) {
      console.log('[App] Already processing an address, request queued via GeocodeRequestManager');
    }

    // Use GeocodeRequestManager for optimized geocoding
    GeocodeRequestManager.geocode(newAddress, {
      onStart: () => {
        isProcessingRef.current = true;
        setLoaded(false);
        setAddress(newAddress);
      },
      onSuccess: (result) => {
        lastProcessedAddressRef.current = newAddress;
        setMapCenter(result.geometry.location);
        setTimeout(() => {
          setLoaded(true);
        }, 500);
      },
      onError: (error) => {
        console.error('[App] Geocode error:', error.message);
        // Still set loaded to true to avoid infinite loading state
        setTimeout(() => {
          setLoaded(true);
        }, 500);
      },
      onComplete: () => {
        isProcessingRef.current = false;
      },
    });
  }, []);

  /**
   * Handles the setLatLng message from parent window
   */
  const handleSetLatLng = useCallback(({ lat, lng }) => {
    alert(`Lat: ${lat}, Lng: ${lng}`);
  }, []);

  /**
   * Message handler for postMessage events
   * Memoized to prevent recreation on every render
   */
  const handleMessage = useCallback((event) => {
    const data = event.data;
    
    // Guard: Invalid data structure
    if (!data || typeof data !== 'object' || !data.func) {
      return;
    }

    // Route to appropriate handler
    switch (data.func) {
      case 'setAddressString':
        handleSetAddressString(data.args || {});
        break;
      case 'setLatLng':
        handleSetLatLng(data.args || {});
        break;
      default:
        // Unknown function, ignore
        break;
    }
  }, [handleSetAddressString, handleSetLatLng]);

  /**
   * Register message event listener once on mount
   * Properly cleans up on unmount to prevent memory leaks and duplicate handlers
   */
  useEffect(() => {
    window.addEventListener("message", handleMessage, false);
    
    // Cleanup function
    return () => {
      window.removeEventListener("message", handleMessage, false);
      // Cancel any pending geocode requests on unmount
      GeocodeRequestManager.cancelPending();
    };
  }, [handleMessage]);

  // Helper function to post messages to parent window
  const postMessage = useCallback((data) => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(data, '*');
    }
  }, []);

  // Preload images on mount
  useEffect(() => {
    preloadImages(PRELOAD_IMAGES);
  }, []);

  // Initial load timer
  useEffect(() => {
    if (!loaded) {
      const timer = setTimeout(() => setLoaded(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [loaded]);

  // Handle reverse geocode results - notify parent of address changes
  useEffect(() => {
    const rooftopAddress = extractRooftopAddress(reverseGeocodeData);
    if (rooftopAddress) {
      postMessage({ type: "address_change", value: rooftopAddress });
    }
  }, [reverseGeocodeData, postMessage]);

  // Save drawing handler with upload mutation
  const saveDrawing = useCallback(
    async (image, original) => {
      try {
        const { imageMeta } = await uploadMutation.mutateAsync({
          image,
          original,
          metadata: {
            mapBounds,
            mapCenter,
            address,
            mode,
            estimate: measurement,
          },
        });

        postMessage({
          type: "estimate",
          value: measurement,
          mode,
          image,
          meta: imageMeta,
        });
      } catch (error) {
        console.error("Failed to save drawing:", error);
      }
    },
    [uploadMutation, mapBounds, mapCenter, address, mode, measurement, postMessage]
  );

  return (
    <ThemeProvider theme={theme}>
      <div className="measurit-container" style={{ overflow: "hidden" }}>
        <MeasurItTitleBar measurement={measurement} mode={mode} />
        
        <WelcomeTour open={tourOpen} onClose={() => setTourOpen(false)} />
        <ColorGuideTour open={drawTourOpen} onClose={() => setDrawTourOpen(false)} />

        {loaded && (
          <Estimator
            setDrawTourOpen={setDrawTourOpen}
            mode={mode}
            updateMode={setMode}
            GOOGLE_API_KEY={GOOGLE_API_KEY}
            mapHeight={mapHeight}
            mapWidth={mapWidth}
            mapCenter={mapCenter}
            updateMapCenter={setMapCenter}
            startingZoomLevel={zoomLevel}
            updateMapBounds={setMapBounds}
            color={COLORS}
            brushSize={BRUSH_SIZES}
            updateCoverageSquareArea={setCoverageSquareArea}
            updateDrawnLinearDistance={setDrawnLinearDistance}
            saveDrawing={saveDrawing}
            measurement={measurement}
          />
        )}

        <Backdrop
          style={{ zIndex: 1001, color: "#fff" }}
          open={uploadMutation.isPending}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </ThemeProvider>
  );
}
